import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { BACKEND_URL } from '../api/axios';
import Navbar from '../pages/Navbar';
import DOMPurify from 'dompurify';

// Same fixed WhatsApp contact used on JobDetail.
const WHATSAPP_NUMBER = '917904949969';

const resolveLogoSrc = (logo) => {
  if (!logo) return null;
  return logo.startsWith('http') ? logo : `${BACKEND_URL}/storage/${logo}`;
};

function formatSalary(min, max, currency) {
  if (!min && !max) return null;
  const symbol = currency === 'INR' ? '₹' : (currency ? `${currency} ` : '');
  if (min && max) return `${symbol}${Number(min).toLocaleString()} - ${symbol}${Number(max).toLocaleString()}`;
  return `${symbol}${Number(min || max).toLocaleString()}`;
}

function formatExperience(min, max) {
  if (min == null && max == null) return null;
  if (min != null && max != null) return `${min}-${max} yrs`;
  return `${min ?? max}+ yrs`;
}

// API responses vary in shape (plain array, {data: [...]}, or paginated).
function extractArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

// Some aggregator sources return double-encoded HTML ("&lt;p&gt;...").
// Decode it, then sanitize so it renders formatted, same as JobDetail.
const decodeEntities = (str) => {
  const el = document.createElement('textarea');
  el.innerHTML = str;
  return el.value;
};

const toSafeHtml = (raw) => {
  if (!raw) return '';
  let html = raw;
  if (/&lt;\/?[a-z][\s\S]*?&gt;/i.test(html)) {
    html = decodeEntities(decodeEntities(html));
  }
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'a'],
    ALLOWED_ATTR: ['href', 'target', 'rel'],
  });
};

// Plain text version of the description, used for the resume-fit check.
const toPlainText = (raw) => {
  if (!raw) return '';
  const el = document.createElement('div');
  el.innerHTML = toSafeHtml(raw);
  return el.textContent || '';
};

function AggregatedJobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [relatedJobs, setRelatedJobs] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const [user] = useState(() => {
    const stored = localStorage.getItem('user');
    return stored ? JSON.parse(stored) : null;
  });

  const [matchScore, setMatchScore] = useState(null);
  const [scoring, setScoring] = useState(false);

  const [insight, setInsight] = useState(null);
  const [loadingInsight, setLoadingInsight] = useState(false);

  const [applying, setApplying] = useState(false);
  const [applied, setApplied] = useState(false);
  const [applyError, setApplyError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchJob = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const response = await api.get(`/aggregated-jobs/${id}`);
        setJob(response.data.data);
        setApplied(Boolean(response.data.data.has_applied));
      } catch (err) {
        console.error('Failed to fetch aggregated job', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetchJob();
  }, [id]);

  // Related jobs: mix of internal and aggregated, prefer same category/location.
  useEffect(() => {
    let ignore = false;

    const fetchRelatedJobs = async () => {
      if (!job) return;
      setLoadingRelated(true);
      try {
        const [internalRes, aggRes] = await Promise.all([
          api.get('/jobs/search'),
          api.get('/aggregated-jobs'),
        ]);

        // Only exclude the current job from the aggregated list; internal
        // jobs have their own id space.
        const internalList = extractArray(internalRes.data).map((j) => ({ ...j, type: 'internal' }));
        const aggList = extractArray(aggRes.data)
          .filter((j) => j.id !== job.id)
          .map((j) => ({ ...j, type: 'aggregated' }));

        const combined = [...internalList, ...aggList];

        const jobCategory = typeof job.category === 'string' ? job.category : job.category?.slug;

        let filtered = combined.filter((j) => {
          const jCategory = typeof j.category === 'string' ? j.category : j.category?.slug;
          const sameCategory = jobCategory && jCategory && jCategory === jobCategory;
          const sameLocation = job.location && j.location && j.location === job.location;
          return sameCategory || sameLocation;
        });

        if (filtered.length < 3) filtered = combined;

        if (!ignore) setRelatedJobs(filtered.slice(0, 6));
      } catch (err) {
        console.error('Failed to fetch related jobs', err);
      } finally {
        if (!ignore) setLoadingRelated(false);
      }
    };

    fetchRelatedJobs();
    return () => { ignore = true; };
  }, [job]);

  // Match score — logged-in job seekers only.
  useEffect(() => {
    const fetchMatchScore = async () => {
      if (!user || user.role !== 'job_seeker' || !job) return;
      if (!localStorage.getItem('token')) return;

      setScoring(true);
      try {
        const res = await api.post('/aggregated-jobs/match-score', { jobs: [job.id] });
        const entry = res.data.find((s) => s.id === job.id);
        setMatchScore(entry ? entry.score : null);
      } catch (err) {
        console.error('Match scoring failed', err);
      } finally {
        setScoring(false);
      }
    };
    fetchMatchScore();
  }, [job, user]);

  // Resume-fit insight, same endpoint JobDetail uses.
  useEffect(() => {
    let ignore = false;

    const fetchInsight = async () => {
      if (!user || user.role !== 'job_seeker' || !job?.description) return;
      setLoadingInsight(true);
      try {
        const res = await api.post('/profile/ats-score', { job_description: toPlainText(job.description) });
        if (!ignore) setInsight(res.data);
      } catch (err) {
        if (!ignore) console.error('Failed to fetch resume insight', err);
      } finally {
        if (!ignore) setLoadingInsight(false);
      }
    };

    fetchInsight();
    return () => { ignore = true; };
  }, [job, user]);

  const handleApply = async () => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'job_seeker') {
      setApplyError('Only job seekers can apply to jobs.');
      return;
    }

    setApplying(true);
    setApplyError('');
    try {
      await api.post(`/aggregated-jobs/${job.id}/apply`);
      setApplied(true);
    } catch (err) {
      if (err.response?.status === 409) {
        setApplied(true);
      } else {
        setApplyError(err.response?.data?.message || 'Failed to apply. Please try again.');
      }
    } finally {
      setApplying(false);
    }
  };

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: job?.title, url });
      } catch (err) {
        // user cancelled the share sheet
      }
    } else {
      await handleCopyLink();
    }
  };

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderRelatedJobCard = (rj) => {
    const isInternal = rj.type === 'internal';
    const linkPath = isInternal ? `/jobs/${rj.id}` : `/aggregated-jobs/${rj.id}`;

    const rjLogoSrc = isInternal
      ? resolveLogoSrc(rj.employer?.company_logo)
      : resolveLogoSrc(rj.company_logo);

    const rjExperience = isInternal
      ? (rj.experience_required != null ? `${rj.experience_required}+ yrs` : null)
      : formatExperience(rj.experience_min, rj.experience_max);

    const rjSkills = isInternal
      ? (Array.isArray(rj.skills_required) ? rj.skills_required.join(', ') : rj.skills_required)
      : rj.skills;

    return (
      <div
        key={`${rj.type}-${rj.id}`}
        className="w-full text-left p-4 rounded-xl border border-gray-200 hover:border-[#9333EA] transition"
      >
        <button onClick={() => navigate(linkPath)} className="w-full text-left">
          <div className="flex items-center gap-3">
            {rjLogoSrc ? (
              <img src={rjLogoSrc} alt="" className="w-12 h-12 rounded-lg object-cover bg-gray-100 shrink-0" />
            ) : (
              <div className="w-12 h-12 rounded-lg bg-gray-100 shrink-0" />
            )}
            <div className="min-w-0">
              <p className="text-base font-semibold text-gray-800 truncate">{rj.title}</p>
              <p className="text-sm text-gray-500 truncate">{rj.company_name} · {rj.location}</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-1.5 mt-3">
            {isInternal && (
              <span className="bg-secondary-green text-primary-green text-xs font-medium px-2.5 py-1 rounded-full">
                On PlanB
              </span>
            )}
            {rj.job_type && (
              <span className="bg-secondary-green text-primary-green text-xs font-medium px-2.5 py-1 rounded-full">
                {rj.job_type.replace(/_/g, ' ')}
              </span>
            )}
            {isInternal && rj.work_mode && (
              <span className="bg-purple-50 text-[#9333EA] text-xs font-medium px-2.5 py-1 rounded-full">
                {rj.work_mode.replace(/_/g, ' ')}
              </span>
            )}
            {rjExperience && (
              <span className="bg-secondary-orange text-primary-orange text-xs font-medium px-2.5 py-1 rounded-full">
                {rjExperience}
              </span>
            )}
          </div>

          {rjSkills && <p className="text-xs text-gray-400 mt-2 truncate">🧩 {rjSkills}</p>}
        </button>

        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate(linkPath);
          }}
          className="mt-3 w-full text-sm font-semibold px-3 py-2 rounded-lg bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white hover:opacity-90"
        >
          View Job
        </button>
      </div>
    );
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading...</div>;
  if (notFound || !job) return <div className="p-10 text-center text-gray-500">Job not found.</div>;

  const logoSrc = resolveLogoSrc(job.company_logo);
  const salary = formatSalary(job.salary_min, job.salary_max, job.salary_currency);
  const experience = formatExperience(job.experience_min, job.experience_max);
  const skills = (job.skills || '').split(',').map((s) => s.trim()).filter(Boolean);
  const matchColor = matchScore >= 80 ? '#10B981' : matchScore >= 50 ? '#7C3AED' : '#F59E0B';
  const whatsappMessage = encodeURIComponent(`Hi, I'm interested in the ${job.title} position.`);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF5FF] to-[#FDF2F8]">
      <Navbar user={user} />

      <div className="max-w-6xl mx-auto px-3 sm:px-4 py-6 sm:py-10">
        <div className="flex flex-col md:flex-row gap-6 items-start">
          {/* Left sidebar: related jobs */}
          <div className="w-72 shrink-0 sticky top-8 hidden md:block">
            <div className="bg-white p-5 rounded-lg shadow">
              <h3 className="font-semibold text-[#1E1033] mb-3">Related jobs</h3>
              {loadingRelated ? (
                <p className="text-sm text-gray-400">Loading related jobs…</p>
              ) : relatedJobs.length > 0 ? (
                <div className="space-y-2">{relatedJobs.map((rj) => renderRelatedJobCard(rj))}</div>
              ) : (
                <p className="text-sm text-gray-400">No related jobs found yet.</p>
              )}
            </div>
          </div>

          {/* Main content: single merged card */}
          <div className="flex-1 w-full min-w-0">
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4 sm:p-8">
              {/* Header row */}
              <div className="flex items-start gap-3">
                {logoSrc ? (
                  <img src={logoSrc} alt="" className="w-11 h-11 rounded-full object-cover shrink-0 border border-gray-100" />
                ) : (
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#7C3AED] to-[#DB2777] flex items-center justify-center font-bold text-white shrink-0">
                    {job.company_name?.charAt(0)?.toUpperCase() || 'J'}
                  </div>
                )}

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-50 text-red-500">
                      {job.job_type?.replace(/_/g, ' ') || 'Open'}
                    </span>
                    {job.source?.name && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-50 text-[#9333EA]">
                        via {job.source.name}
                      </span>
                    )}
                  </div>

                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <h2 className="font-bold text-gray-900 leading-snug text-lg">{job.title}</h2>
                      <p className="text-sm text-gray-500">{job.company_name}</p>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
                      {matchScore != null && (
                        <span
                          className="text-xs font-bold px-3 py-1.5 rounded-full text-white whitespace-nowrap mr-1"
                          style={{ backgroundColor: matchColor }}
                        >
                          {matchScore}% match
                        </span>
                      )}

                      {/* WhatsApp */}
                      <a
                        href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
                        target="_blank"
                        rel="noreferrer"
                        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-[#25D366]"
                        title="Message on WhatsApp"
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                          <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.33 4.95L2 22l5.29-1.39a9.9 9.9 0 0 0 4.75 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C21.96 6.45 17.51 2 12.04 2zm5.8 14.02c-.24.68-1.4 1.32-1.93 1.4-.5.08-1.12.11-1.8-.11a17.6 17.6 0 0 1-1.66-.61c-2.94-1.27-4.86-4.24-5-4.44-.15-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.46.27-.3.59-.37.79-.37h.57c.18 0 .43-.07.67.51.24.58.83 2.01.9 2.15.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.39-.44.52-.15.15-.3.31-.13.61.17.3.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.36 1.46.3.15.47.13.65-.08.18-.2.76-.89.96-1.19.2-.3.4-.25.67-.15.27.1 1.72.81 2.01.96.3.15.5.23.57.35.08.13.08.72-.16 1.4z" />
                        </svg>
                      </a>

                      {/* Share */}
                      <button
                        onClick={handleShare}
                        className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700"
                        title={copied ? 'Copied!' : 'Share'}
                      >
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                          <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L7.04 9.81C6.5 9.31 5.79 9 5 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z" />
                        </svg>
                      </button>

                      {job.salary_max && (
                        <span className="text-sm font-bold text-gray-900 ml-1 whitespace-nowrap">
                          {job.salary_currency === 'INR' || !job.salary_currency ? '₹' : `${job.salary_currency} `}
                          {Math.round(job.salary_max / 1000)}K
                        </span>
                      )}
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                    {job.job_type && <span>{job.job_type.replace(/_/g, ' ')}</span>}
                    {experience && <span>· {experience} exp</span>}
                    {job.category && <span>· {job.category}</span>}
                  </p>
                </div>
              </div>

              {scoring && (
                <p className="text-xs text-[#7C3AED] mt-2">Scoring this job against your profile…</p>
              )}

              {/* Resume fit row */}
              {user?.role === 'job_seeker' && (
                <div className="mt-3">
                  {loadingInsight ? (
                    <p className="text-xs text-gray-400">Checking your fit for this role…</p>
                  ) : insight ? (
                    <div className="flex flex-col gap-1.5 text-xs">
                      {insight.matched_keywords?.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap font-medium text-gray-500">
                          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-green-500">✓</span>
                          Your match!
                          <span className="flex flex-wrap gap-1 ml-1">
                            {insight.matched_keywords.map((s, i) => (
                              <span key={i} className="flex items-center gap-0.5 text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-600">✓ {s}</span>
                            ))}
                          </span>
                        </div>
                      )}
                      {insight.missing_keywords?.length > 0 && (
                        <div className="flex items-center gap-1.5 flex-wrap font-medium text-gray-500">
                          <span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white bg-amber-500">!</span>
                          Worth adding
                          <span className="flex flex-wrap gap-1 ml-1">
                            {insight.missing_keywords.map((s, i) => (
                              <span key={i} className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-600">{s}</span>
                            ))}
                          </span>
                        </div>
                      )}
                      {insight.suggestions?.length > 0 && (
                        <ul className="mt-1 list-disc list-inside text-gray-500 space-y-0.5">
                          {insight.suggestions.map((s, i) => <li key={i}>{s}</li>)}
                        </ul>
                      )}
                    </div>
                  ) : null}
                </div>
              )}

              {/* 3-column layout: meta / description / share panel */}
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-[140px_1fr_200px] gap-4">
                <div className="space-y-1.5 text-xs text-gray-500">
                  <p>📍 {job.location || 'Not specified'}{job.country ? `, ${job.country}` : ''}</p>
                  {job.job_type && <p>⏰ {job.job_type.replace(/_/g, ' ')}</p>}
                  {experience && <p>💼 {experience} exp</p>}
                  {salary && <p>💰 {salary}</p>}
                </div>

                <div>
                  {skills.length > 0 && (
                    <div className="mb-4">
                      <p className="text-xs font-semibold text-gray-400 uppercase mb-1.5">Skills</p>
                      <div className="flex flex-wrap gap-1.5">
                        {skills.map((s, i) => (
                          <span key={i} className="text-[11px] font-medium px-2.5 py-1 rounded-full bg-purple-50 text-[#9333EA]">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  <p className="text-xs font-semibold text-gray-400 uppercase mb-1">What you'll be working on</p>
                  {job.description ? (
                    <div
                      className="text-sm text-gray-600 leading-relaxed [&_p]:mb-3 [&_strong]:font-semibold [&_ul]:list-disc [&_ul]:pl-5"
                      dangerouslySetInnerHTML={{ __html: toSafeHtml(job.description) }}
                    />
                  ) : (
                    <p className="text-sm text-gray-600">No description provided.</p>
                  )}
                </div>

                <div className="bg-gray-50 rounded-xl p-3 h-fit space-y-3">
                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-2">Contact us</p>
                    <a
                      href={`https://wa.me/${WHATSAPP_NUMBER}?text=${whatsappMessage}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-1.5 text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-[#25D366] text-white"
                    >
                      <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor">
                        <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.33 4.95L2 22l5.29-1.39a9.9 9.9 0 0 0 4.75 1.21h.01c5.46 0 9.9-4.45 9.9-9.91C21.96 6.45 17.51 2 12.04 2zm5.8 14.02c-.24.68-1.4 1.32-1.93 1.4-.5.08-1.12.11-1.8-.11a17.6 17.6 0 0 1-1.66-.61c-2.94-1.27-4.86-4.24-5-4.44-.15-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.46.27-.3.59-.37.79-.37h.57c.18 0 .43-.07.67.51.24.58.83 2.01.9 2.15.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.39-.44.52-.15.15-.3.31-.13.61.17.3.76 1.26 1.63 2.04 1.12 1 2.06 1.31 2.36 1.46.3.15.47.13.65-.08.18-.2.76-.89.96-1.19.2-.3.4-.25.67-.15.27.1 1.72.81 2.01.96.3.15.5.23.57.35.08.13.08.72-.16 1.4z" />
                      </svg>
                      WhatsApp
                    </a>
                  </div>

                  <div>
                    <p className="text-xs font-semibold text-gray-700 mb-2">↗ Share this job</p>
                    <div className="flex items-center gap-1.5">
                      <input
                        readOnly
                        value={window.location.href}
                        className="flex-1 min-w-0 text-[11px] border rounded-lg px-2 py-1.5 bg-white text-gray-500 truncate"
                      />
                      <button
                        onClick={handleCopyLink}
                        className="text-[11px] font-semibold px-2.5 py-1.5 rounded-lg bg-gray-900 text-white whitespace-nowrap shrink-0"
                      >
                        {copied ? 'Copied!' : 'Copy link'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Apply section */}
              {(!user || user.role === 'job_seeker') && (
                <>
                  <hr className="my-6" />

                  {applied ? (
                    <div className="bg-secondary-green text-primary-green px-4 py-3 rounded-lg text-sm font-semibold">
                      ✓ You have already applied to this job. We'll confirm with the employer.
                    </div>
                  ) : (
                    <>
                      <h3 className="font-semibold text-[#1E1033] mb-3">Apply to this job</h3>

                      {applyError && (
                        <div className="p-3 rounded mb-4 text-sm bg-red-100 text-red-700">{applyError}</div>
                      )}

                      <button
                        onClick={handleApply}
                        disabled={applying}
                        className="w-full sm:w-auto bg-gradient-to-r from-[#7C3AED] to-[#DB2777] text-white font-semibold px-6 py-2 rounded hover:opacity-90 disabled:opacity-50"
                      >
                        {applying ? 'Submitting...' : 'Apply Now'}
                      </button>
                    </>
                  )}

                  <p className="text-[10px] text-gray-400 mt-4 leading-snug">
                    This job was found on {job.source?.name || 'an external source'} and is not posted directly by PlanB Jobs.
                    Our team will confirm the opportunity with the employer after you apply.
                  </p>
                </>
              )}

              {user && user.role === 'employer' && (
                <p className="mt-6 text-sm text-gray-500 italic">Employers cannot apply to job postings.</p>
              )}

              {user && user.role === 'admin' && (
                <p className="mt-6 text-sm text-gray-500 italic">
                  Admin view — application form is hidden for admin accounts.
                </p>
              )}

              {/* Related jobs: mobile fallback */}
              {relatedJobs.length > 0 && (
                <div className="md:hidden">
                  <hr className="my-6" />
                  <h3 className="font-semibold text-[#1E1033] mb-2">Related jobs</h3>
                  <div className="space-y-2">{relatedJobs.map((rj) => renderRelatedJobCard(rj))}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AggregatedJobDetail;